import { useEffect, useState } from "react";
import { Mail, Menu, X } from "lucide-react";
import { BUSINESS } from "../../lib/site-content";

const NAV_LINKS = [
  { id: "about", label: "About" },
  { id: "residential", label: "Residential" },
  { id: "commercial", label: "Commercial" },
  { id: "stones", label: "Stones" },
  { id: "services", label: "Services" },
  { id: "before-after", label: "Gallery" },
  { id: "schedule", label: "Schedule" },
  { id: "contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLink = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#f0ece4]/95 backdrop-blur-xl border-b border-stone-300/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between gap-4">

        {/* Left — logo + name */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 shrink-0"
          aria-label="The Stone Remedy — Home"
        >
          <img
            src={BUSINESS.logo}
            alt="The Stone Remedy"
            className="h-12 md:h-14 w-auto object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="flex flex-col leading-none text-left hidden sm:flex">
            <span className="font-serif text-sm tracking-widest text-stone-900 whitespace-nowrap">The Stone Remedy</span>
            <span className="text-[8px] tracking-[0.25em] uppercase text-stone-500 mt-0.5 whitespace-nowrap">Natural Stone Restoration</span>
          </div>
        </button>

        {/* Right — nav links + contact buttons + mobile hamburger */}
        <div className="flex items-center gap-3">
          <nav className="hidden xl:flex items-center gap-5">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => handleLink(l.id)}
                className="text-[11px] font-medium tracking-wide text-stone-700 hover:text-stone-900 transition-colors whitespace-nowrap"
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <a
              href={BUSINESS.emailHref}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-white bg-stone-900 hover:bg-stone-800 transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>Email</span>
            </a>
          </div>
          <button
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden inline-flex items-center justify-center w-10 h-10 text-stone-900"
            aria-label="Open menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

      </div>

      {open && (
        <div className="xl:hidden fixed inset-0 top-16 bg-[#f0ece4] z-40 flex flex-col">
          <nav className="flex flex-col px-6 pt-6 pb-4 gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => handleLink(l.id)}
                className="text-left font-serif text-2xl text-stone-900 py-3 border-b border-stone-100"
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="mt-auto px-6 pb-10 flex flex-col gap-3">
            <a
              href={BUSINESS.emailHref}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 border border-stone-900 text-stone-900 text-sm tracking-wider"
            >
              <Mail className="w-4 h-4" />
              Email Us
            </a>
            <p className="text-center text-[11px] tracking-[0.22em] uppercase text-stone-500 pt-2">
              {BUSINESS.serviceArea}
            </p>
          </div>
        </div>
      )}
    </header>
  );
}
